// React and core dependencies
import React, { useEffect, useMemo } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { notification } from 'antd';
import './App.css';

// Authentication & Context
import { useStateContext } from './contexts/ContextProvider';
import { useFetchGetProfile } from './hooks/useAuthen';
import { useQueryClient } from '@tanstack/react-query';

// Components
import { Sidebar, Dashboard } from './components';
import PrivateRoute from './components/PrivateRoute';
import MainContent from './components/MainContent';

// Pages
import Login from './modules/Login/page';
import WebSocketTest from './modules/WebSocketTest/page';

// WebSocket Service
import websocketService from './services/websocket';
import { refreshTokenIfExpiringSoon } from './services/index';

//backoffice
import EmployeesBackoffice from './modules/back-office/Employees/page';
import Company from './modules/back-office/Companies/page';

// Workspace Pages
import Schedule from './modules/workspace/Schedule/page';
import Case from './modules/workspace/Case/page';
import Portal from './modules/workspace/Portal/page';
import Input from './modules/workspace/Input/page';
import LineCasePage from './modules/workspace/LineCase/page';
import DashboardPage from './modules/workspace/dashboard/page';
import Audit from './modules/workspace/Audit/page';
import ErrorSummary from './modules/workspace/ErrorSummary/page';
import AuditCase from './modules/workspace/AuditCase/page';
import Report from './modules/workspace/Report/page';
import AuditCaseReport from './modules/workspace/AuditCaseReport/page';
import EmployeeCaseReport from './modules/workspace/EmployeeCaseReport/page';
import WorkloadReport from './modules/workspace/WorkloadReport/page';
import KpiReport from './modules/workspace/KpiReport/page';
import AttendanceReport from './modules/workspace/AttendanceReport/page';
import AttendanceReportMonthly from './modules/workspace/AttendanceReportMonthly/page';
import ActivityLog from './modules/workspace/ActivityLog/page';
import BirthdayPromo from './modules/workspace/BirthdayPromo/page';
import UserBank from './modules/workspace/UserBank/page';
import PickupTimeReport from './modules/workspace/PickupTimeReport/page';
import ManualTimeclock from './modules/workspace/ManualTimeclock/page';
import VIPMasterData from './modules/workspace/VIPMasterData/page';
import BlacklistPage from './modules/workspace/Blacklist/page';
import TelegramPage from './modules/workspace/Telegram/page';
import ReportAdmin from './modules/workspace/ReportAdmin/page';
import ReportAdminInputs from './modules/workspace/ReportAdminInputs/page';

// Admin Pages
import AddCaseSetting from './modules/admin/AddCaseSetting/page';
import Employees from './modules/admin/Employees/page';
import CustomfieldCase from './modules/admin/Customfield/Case/page';
import Setting from './modules/admin/Setting/page';
import SystemJob from './modules/admin/SystemJob/page';
import LineChannel from './modules/admin/LineChannel/page';
import TelegramBot from './modules/admin/TelegramBot/page';
import TelegramChatTarget from './modules/admin/TelegramChatTarget/page';
import EmployeesTimeline from './modules/workspace/Employees-Timeline/page';
import InputPage from './modules/admin/Masterdata/Input/page';
import InputAuditPage from './modules/admin/Masterdata/InputAudit/page';
import BlacklistTopicPage from './modules/admin/Masterdata/BlacklistTopic/page';
import ErrorTopicPage from './modules/admin/Masterdata/ErrorTopic/page';
import NotificationSettingPage from './modules/admin/Masterdata/NotificationSetting/page';
import ScheduleAdmin from './modules/admin/Schedule/page';

const App = () => {
  const queryClient = useQueryClient();
  const { setCurrentColor, setCurrentMode, setGetProfile } = useStateContext();
  const { data: getProfile, isLoading: isLoadingGetProfile, error } = useFetchGetProfile();
  const currentUserId = parseInt(sessionStorage.getItem("id") || 0);
  const token = sessionStorage.getItem('token');
  useEffect(() => {
    if (getProfile) {
      setGetProfile(getProfile);
    }
    if(currentUserId == 0 && window.location.pathname != "/"){
      if(token){
        window.location.href = "/";
      }
    }
    // Setup WebSocket connection
    websocketService.connect();
    
    return () => {
      websocketService.disconnect();
    };
  }, [getProfile, setGetProfile]);

  // Realtime: เมื่อมีเคสเปลี่ยน (push 'case:updated') → refetch list + badge
  // ใช้ throttle (leading+trailing) กัน "stampede" ตอนหลายคนทำ action ถี่ ๆ พร้อมกัน
  // (ไม่งั้นทุก client จะ refetch query หนักพร้อมกันทุก action → connection pool ตัน)
  useEffect(() => {
    const THROTTLE_MS = 5000;
    let lastRun = 0;
    let trailingTimer = null;

    const doInvalidate = () => {
      lastRun = Date.now();
      queryClient.invalidateQueries(['getFetchCase']);
      queryClient.invalidateQueries(['getCaseCount']);
      // คิวเคสตีกลับ/ขอข้อมูลเพิ่ม ของหน้า "ส่งข้อมูลตรวจสอบ" — เดิมไม่มีอะไรสั่งให้โหลดใหม่
      // คนที่เปิดหน้าค้างไว้จึงเห็นเคสที่เพื่อนเติมข้อมูลส่งกลับไปแล้ว และกดส่งซ้ำได้
      // (react-query refetch เฉพาะ query ที่มีหน้าจอใช้งานอยู่ คนที่ไม่ได้เปิดหน้านี้ไม่โดน)
      queryClient.invalidateQueries(['getFetchListInputReject']);
      // หน้า "เคสจาก LINE" เป็นคิวงานเหมือนกัน (เคส LINE ถูกแยกออกจากหน้าข้างบน)
      queryClient.invalidateQueries(['getFetchLineCaseQueue']);
    };

    const handleCaseUpdated = (data) => {
      const myCompany = sessionStorage.getItem('activeCompanyId');
      // เฉพาะเคสของบริษัทตัวเอง (หรือ event ที่ไม่ระบุบริษัท)
      if (data?.company_id && String(data.company_id) !== String(myCompany)) return;

      const elapsed = Date.now() - lastRun;
      if (elapsed >= THROTTLE_MS) {
        // leading: action แรก/หลังเว้นช่วง → update ทันที (ยังรู้สึก realtime)
        if (trailingTimer) {
          clearTimeout(trailingTimer);
          trailingTimer = null;
        }
        doInvalidate();
      } else if (!trailingTimer) {
        // trailing: action ถี่ในช่วง throttle → รวบ refetch อีกครั้งตอนจบ window
        trailingTimer = setTimeout(() => {
          trailingTimer = null;
          doInvalidate();
        }, THROTTLE_MS - elapsed);
      }
    };

    websocketService.onCaseEvent(handleCaseUpdated);
    return () => {
      if (trailingTimer) clearTimeout(trailingTimer);
      websocketService.offCaseEvent(handleCaseUpdated);
    };
  }, [queryClient]);

  // แจ้งเตือนระบบ (เช่น cookie ของระบบเชื่อมต่ออัตโนมัติหมดอายุ) — เด้ง toast ให้ "admin" เท่านั้น
  useEffect(() => {
    const handleNotification = (data) => {
      if (!data || data.category !== 'system_job') return; // สนใจเฉพาะแจ้งเตือนระบบ job
      // เห็นเฉพาะ "Head ขึ้นไป" = Admin/Super Admin (userRole 1,2) หรือ พนักงานที่เป็น Head (jobType 5)
      const role = Number(sessionStorage.getItem('role'));
      const jobType = Number(sessionStorage.getItem('jobType'));
      const canSee = [1, 2].includes(role) || jobType === 5; // 5 = Head
      if (!canSee) return;
      const myCompany = sessionStorage.getItem('activeCompanyId');
      if (data.company_id && String(data.company_id) !== String(myCompany)) return; // เฉพาะบริษัทตัวเอง
      notification[data.type === 'error' ? 'error' : 'warning']({
        message: data.title || 'แจ้งเตือนระบบ',
        description: data.message,
        duration: 0, // ค้างไว้จนกดปิด (เป็นเรื่องต้อง action)
      });
    };
    websocketService.onNotification(handleNotification);
    return () => websocketService.offNotification(handleNotification);
  }, []);

  // ต่ออายุ token ล่วงหน้า — เช็คทุก 1 นาที ถ้าเหลือน้อยกว่า 5 นาทีค่อยขอใหม่
  // (ไม่ผูกกับการคลิก/พิมพ์แบบเดิม เพราะคนที่เปิดหน้าจอทิ้งไว้เฉย ๆ ก็ต้องไม่หลุด)
  // เช็คตอนกลับมาที่แท็บด้วย — เครื่อง sleep ไปแล้ว timer จะไม่ทันเวลา
  useEffect(() => {
    let stopped = false;
    const tick = () => { if (!stopped) refreshTokenIfExpiringSoon(); };
    tick();
    const timer = setInterval(tick, 60 * 1000);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);


  // useEffect(() => {
  //   if (error || (!getProfile && !isLoadingGetProfile)) {
  //     sessionStorage.removeItem('token');
  //   }
  // }, [error, getProfile, isLoadingGetProfile]);

  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

const AppContent = () => {
  const location = useLocation();
  const { activeMenu } = useStateContext();
  const isLoginRoute = useMemo(() => location.pathname === '/', [location.pathname]);

  return (
    <div className="flex relative dark:bg-main-dark-bg">
      {!isLoginRoute && (
        <div className={activeMenu ? "fixed sidebar dark:bg-gray-800 bg-white z-999" : "w-0 dark:bg-gray-800 z-999"}>
          <Sidebar />
        </div>
      )}
      <MainContent isLoginRoute={isLoginRoute} activeMenu={activeMenu}>
        <Routes>
          <Route path="/" element={<Login />} /> 
          <Route path="/workspace/schedule" element={<PrivateRoute><Schedule /></PrivateRoute>} /> 
          <Route path="/workspace/case" element={<PrivateRoute><Case /></PrivateRoute>} /> 
          {/* หน้าเลือกบริษัทของคนที่สังกัดหลายบริษัท — เปิดแท็บละบริษัท */}
          <Route path="/workspace/portal" element={<PrivateRoute><Portal /></PrivateRoute>} />
          <Route path="/workspace/input" element={<PrivateRoute><Input /></PrivateRoute>} /> 
          <Route path="/workspace/lineCase" element={<PrivateRoute><LineCasePage /></PrivateRoute>} />
          <Route path="/workspace/employees-timeline" element={<PrivateRoute><EmployeesTimeline /></PrivateRoute>} /> 
          <Route path="/workspace/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
          <Route path="/workspace/audit" element={<PrivateRoute><Audit /></PrivateRoute>} />
          <Route path="/workspace/errorSummary" element={<PrivateRoute><ErrorSummary /></PrivateRoute>} />
          <Route path="/workspace/auditCase" element={<PrivateRoute><AuditCase /></PrivateRoute>} />
          <Route path="/workspace/report" element={<PrivateRoute><Report /></PrivateRoute>} />
          <Route path="/workspace/auditCaseReport" element={<PrivateRoute><AuditCaseReport /></PrivateRoute>} />
          <Route path="/workspace/employeeCaseReport" element={<PrivateRoute><EmployeeCaseReport /></PrivateRoute>} />
          <Route path="/workspace/workloadReport" element={<PrivateRoute><WorkloadReport /></PrivateRoute>} />
          <Route path="/workspace/kpiReport" element={<PrivateRoute><KpiReport /></PrivateRoute>} />
          <Route path="/workspace/attendanceReport" element={<PrivateRoute><AttendanceReport /></PrivateRoute>} />
          <Route path="/workspace/attendanceReportMonthly" element={<PrivateRoute><AttendanceReportMonthly /></PrivateRoute>} />
          <Route path="/workspace/activityLog" element={<PrivateRoute><ActivityLog /></PrivateRoute>} />
          <Route path="/workspace/birthdayPromo" element={<PrivateRoute><BirthdayPromo /></PrivateRoute>} />
          <Route path="/workspace/userBank" element={<PrivateRoute><UserBank /></PrivateRoute>} />
          <Route path="/workspace/pickupTime" element={<PrivateRoute><PickupTimeReport /></PrivateRoute>} />
          <Route path="/workspace/vipMasterData" element={<PrivateRoute><VIPMasterData /></PrivateRoute>} />
          <Route path="/workspace/blacklist" element={<PrivateRoute><BlacklistPage /></PrivateRoute>} />
          <Route path="/workspace/telegram" element={<PrivateRoute><TelegramPage /></PrivateRoute>} />
          <Route path="/workspace/manualTimeclock" element={<PrivateRoute><ManualTimeclock /></PrivateRoute>} />
          <Route path="/workspace/reportAdmin" element={<PrivateRoute><ReportAdmin /></PrivateRoute>} />
          <Route path="/workspace/reportAdminInputs" element={<PrivateRoute><ReportAdminInputs /></PrivateRoute>} />
          
          <Route path="/admin/employees" element={<PrivateRoute><Employees /></PrivateRoute>} />
          <Route path="/admin/addCaseSetting" element={<PrivateRoute><AddCaseSetting /></PrivateRoute>} /> 
          <Route path="/admin/setting" element={<PrivateRoute><Setting /></PrivateRoute>} />
          <Route path="/admin/systemJob" element={<PrivateRoute><SystemJob /></PrivateRoute>} />
          <Route path="/admin/lineChannel" element={<PrivateRoute><LineChannel /></PrivateRoute>} />
          <Route path="/admin/telegramBot" element={<PrivateRoute><TelegramBot /></PrivateRoute>} />
          <Route path="/admin/telegramChatTarget" element={<PrivateRoute><TelegramChatTarget /></PrivateRoute>} />
          <Route path="/admin/masterdata/input" element={<PrivateRoute><InputPage /></PrivateRoute>} />
          <Route path="/admin/masterdata/inputAudit" element={<PrivateRoute><InputAuditPage /></PrivateRoute>} />
          <Route path="/admin/masterdata/blacklistTopic" element={<PrivateRoute><BlacklistTopicPage /></PrivateRoute>} />
          <Route path="/admin/masterdata/errorTopic" element={<PrivateRoute><ErrorTopicPage /></PrivateRoute>} />
          <Route path="/admin/masterdata/notification" element={<PrivateRoute><NotificationSettingPage /></PrivateRoute>} />
          <Route path="/admin/schedule" element={<PrivateRoute><ScheduleAdmin /></PrivateRoute>} />

          <Route path="/backoffice/employees" element={<PrivateRoute><EmployeesBackoffice /></PrivateRoute>} />
          <Route path="/backoffice/companies" element={<PrivateRoute><Company /></PrivateRoute>} />
          <Route path="/admin/customfield/case" element={<PrivateRoute><CustomfieldCase /></PrivateRoute>} />
          <Route path="/websocket-test" element={<PrivateRoute><WebSocketTest /></PrivateRoute>} />
          {/* <DYNAMIC_PAGE_ROUTES> placeholder for dynamic routes */}				
          </Routes>
      </MainContent>
      
    </div>
  );
};

export default App;
